import type { Role } from '@/types';
import { Button } from '@/components/ui';
import { Pencil, Trash2, KeyRound, Shield } from 'lucide-react';

interface RoleCardProps {
  role: Role;
  onEdit: (role: Role) => void;
  onDelete: (role: Role) => void;
  onPermissions?: (role: Role) => void;
}

export default function RoleCard({ role, onEdit, onDelete, onPermissions }: RoleCardProps) {
  const roleName = role.nom ?? role.name ?? role.code ?? `Rôle #${role.id}`;
  const permissionCount = Array.isArray(role.permissions) ? role.permissions.length : 0;
  return (
    <article
      className="group relative flex flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm transition-all duration-200 hover:border-slate-300 hover:shadow-md"
    >
      {/* Accent bar */}
      <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-indigo-500 to-indigo-600" />

      <div className="flex flex-1 flex-col gap-4 pl-5 pr-4 pt-4 pb-3">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white text-indigo-600 shadow-sm">
            <Shield className="h-6 w-6" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <h3 className="truncate text-base font-semibold tracking-tight text-slate-900">
              {roleName}
            </h3>
            <p className="mt-1 text-sm font-medium text-slate-500">
              {permissionCount} permission{permissionCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
          {onPermissions && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPermissions(role)}
              className="flex-1 min-w-0 border-slate-200 bg-slate-50/50 font-medium text-slate-700 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 sm:flex-none"
              aria-label="Gérer les permissions"
            >
              <KeyRound className="h-4 w-4 shrink-0" />
            </Button>
          )}
          <div className="flex gap-1">
            <Button
              variant="update"
              size="sm"
              onClick={() => onEdit(role)}
              className="text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Modifier"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => onDelete(role)}
              aria-label="Supprimer"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}
